import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  Dispatch,
  SetStateAction,
} from "react";
import { SearchEntityType } from "@/models/search/Search";
import {
  LegislationSearchResult,
  WorkspaceDocumentSearchResult,
} from "@/models/search/SearchResults";
import GlobalSearchCategorySection from "./GlobalSearchCategorySection";
import GlobalSearchFilters from "./GlobalSearchFilters";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { LeveledFiltersConfig } from "@/components/data-table/data-table";
import { StaleSearchIndexNotice } from "@/components/indexing-status/StaleSearchIndexNotice";
import {
  StaleIndexDocument,
  useStaleSearchIndex,
} from "@/hooks/useStaleSearchIndex";

interface GlobalSearchResultsViewProps {
  expandedCategory: SearchEntityType | null;
  onExpandCategory: (category: SearchEntityType | null) => void;
  legislationResults?: LegislationSearchResult[];
  workspaceDocResults?: WorkspaceDocumentSearchResult[];
  isLoadingLegislation?: boolean;
  isLoadingWorkspaceDocs?: boolean;
  legislationHasMore?: boolean;
  workspaceDocsHasMore?: boolean;
  onResultClick?: () => void;
  // Filter configuration and state
  leveledFilters?: LeveledFiltersConfig;
  defaultsPlan: {
    sourceKey: string;
    childFilters: { id: string; value: unknown }[];
  } | null;
  sources: string[];
  setSources: (sources: string[]) => void;
  filters: Record<string, string[]>;
  workspaceDocumentFilters?: Record<string, string[]>;
  onFilterChange: (filterKey: string, values: string[]) => void;
  sourceCounts?: Record<string, number>;
  focusedSource: string | null;
  setFocusedSource: Dispatch<SetStateAction<string | null>>;
  legislationTotalRecords?: number;
  documentTotalRecords?: number;
  // Expanded view infinite scroll support
  onLoadMore: (category: SearchEntityType) => void;
  isLoadingMore: boolean;
  expandedLegislationResults?: LegislationSearchResult[];
  expandedDocumentResults?: WorkspaceDocumentSearchResult[];
}

const GlobalSearchResultsView = ({
  expandedCategory,
  onExpandCategory,
  legislationResults,
  workspaceDocResults,
  isLoadingLegislation = false,
  isLoadingWorkspaceDocs = false,
  legislationHasMore = false,
  workspaceDocsHasMore = false,
  onResultClick,
  leveledFilters,
  defaultsPlan,
  sources,
  setSources,
  filters,
  workspaceDocumentFilters = {},
  onFilterChange,
  sourceCounts,
  focusedSource,
  setFocusedSource,
  legislationTotalRecords,
  documentTotalRecords,
  onLoadMore,
  isLoadingMore,
  expandedLegislationResults,
  expandedDocumentResults,
}: GlobalSearchResultsViewProps) => {
  // Refs for section containers to measure height (for overview mode)
  const legislationSectionRef = useRef<HTMLDivElement>(null);
  const workspaceDocsSectionRef = useRef<HTMLDivElement>(null);
  const [maxVisibleLegislation, setMaxVisibleLegislation] = useState(3);
  const [maxVisibleWorkspaceDocs, setMaxVisibleWorkspaceDocs] = useState(3);

  // Outdated search indexes among the document hits on screen (LAR-235): the same notice
  // and rerun as the files tab and the Workspaces page search. The overview only has room
  // for one line; the expanded list gets the full explanation.
  const shownDocumentResults = useMemo(() => {
    if (expandedCategory === SearchEntityType.WorkspaceDocument) {
      return (expandedDocumentResults?.length ?? 0) > 0
        ? (expandedDocumentResults ?? [])
        : (workspaceDocResults ?? []);
    }
    if (expandedCategory !== null) return [];
    return (workspaceDocResults ?? []).slice(0, maxVisibleWorkspaceDocs);
  }, [
    expandedCategory,
    expandedDocumentResults,
    workspaceDocResults,
    maxVisibleWorkspaceDocs,
  ]);
  const staleIndexDocuments = useMemo<StaleIndexDocument[]>(
    () =>
      shownDocumentResults.map((document) => ({
        id: document.id,
        workspaceId: document.workspaceId,
        searchIndexStale: document.searchIndexStale ?? false,
        canReindex: document.canReindex ?? false,
      })),
    [shownDocumentResults]
  );
  const staleIndex = useStaleSearchIndex(staleIndexDocuments);
  const showWorkspaceDocsNotice = staleIndex.staleCount > 0;

  // Constants for height calculation
  const HEADER_HEIGHT = 44; // approx height of section header
  const ITEM_HEIGHT = 64; // approx height of each result item
  const BUTTON_HEIGHT = 40; // approx height of "Se flere" button

  // Calculate visible items based on container height
  const calculateVisibleItems = useCallback(() => {
    if (legislationSectionRef.current) {
      const containerHeight = legislationSectionRef.current.clientHeight;
      // Only update if we have a reasonable container height
      if (containerHeight > 100) {
        const availableHeight = containerHeight - HEADER_HEIGHT - BUTTON_HEIGHT;
        const itemsThatFit = Math.max(
          1,
          Math.min(10, Math.floor(availableHeight / ITEM_HEIGHT))
        );
        setMaxVisibleLegislation(itemsThatFit);
      }
    }
    if (workspaceDocsSectionRef.current) {
      const containerHeight = workspaceDocsSectionRef.current.clientHeight;
      // Only update if we have a reasonable container height
      if (containerHeight > 100) {
        // The stale-index notice is not subtracted here on purpose: which hits are
        // visible decides whether the notice shows, so letting it shrink the budget
        // would let it toggle itself forever. Flexbox gives it room instead.
        const availableHeight = containerHeight - HEADER_HEIGHT - BUTTON_HEIGHT;
        const itemsThatFit = Math.max(
          1,
          Math.min(10, Math.floor(availableHeight / ITEM_HEIGHT))
        );
        setMaxVisibleWorkspaceDocs(itemsThatFit);
      }
    }
  }, []);

  useLayoutEffect(() => {
    let resizeObserver: ResizeObserver | null = null;

    // Small delay to ensure layout is complete
    const timeoutId = setTimeout(() => {
      calculateVisibleItems();

      resizeObserver = new ResizeObserver(calculateVisibleItems);
      if (legislationSectionRef.current)
        resizeObserver.observe(legislationSectionRef.current);
      if (workspaceDocsSectionRef.current)
        resizeObserver.observe(workspaceDocsSectionRef.current);
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [calculateVisibleItems]);

  // Helper to determine if "Show more" button should appear
  const shouldShowExpandButton = (
    results:
      (LegislationSearchResult | WorkspaceDocumentSearchResult)[] | undefined,
    hasMore: boolean,
    maxResults: number
  ) => {
    if (!results) return false;
    return hasMore || results.length > maxResults;
  };

  // Helper to render the "Se flere" button
  const renderShowMoreButton = (onClick: () => void) => (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center justify-center gap-1 py-2 px-3",
        "text-sm text-primary hover:text-accent-foreground hover:bg-accent rounded-sm",
        "transition-colors cursor-pointer"
      )}
    >
      <span>Se flere resultater</span>
      <ChevronRight className="w-4 h-4" />
    </button>
  );

  // Intersection observer for infinite scroll in expanded view
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoadingMore) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          const hasMore =
            expandedCategory === SearchEntityType.Legislation
              ? legislationHasMore
              : workspaceDocsHasMore;
          if (hasMore && expandedCategory) {
            onLoadMore(expandedCategory);
          }
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [
      isLoadingMore,
      onLoadMore,
      expandedCategory,
      legislationHasMore,
      workspaceDocsHasMore,
    ]
  );

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleBack = () => {
    onExpandCategory(null);
  };

  const isExpanded = expandedCategory !== null;

  return (
    <div className="flex flex-col h-full">
      {/* Main content area */}
      <div className="flex flex-1 min-h-0">
        {/* Left sidebar - Filters */}
        <div
          className={cn(
            "w-80 shrink-0 border-r border-border p-4 overflow-y-auto",
            "hidden md:block"
          )}
        >
          <GlobalSearchFilters
            leveledFilters={leveledFilters}
            filters={filters}
            workspaceDocumentFilters={workspaceDocumentFilters}
            defaultsPlan={defaultsPlan}
            onFilterChange={onFilterChange}
            sources={sources}
            setSources={setSources}
            sourceCounts={sourceCounts}
            focusedSource={focusedSource}
            setFocusedSource={setFocusedSource}
          />
        </div>

        {/* Right content - Results */}
        <div className="flex-1 min-h-0 p-4 flex flex-col overflow-hidden">
          {!isExpanded ? (
            // Overview mode - show both categories in 50/50 split (no scroll)
            <div className="flex flex-col h-full gap-2">
              {/* Legislation Section */}
              <div
                ref={legislationSectionRef}
                className="flex-1 min-h-0 flex flex-col overflow-hidden"
              >
                {/* Items container - clips overflow */}
                <div className="flex-1 min-h-0 overflow-hidden">
                  <GlobalSearchCategorySection
                    entityType={SearchEntityType.Legislation}
                    results={legislationResults}
                    isLoading={isLoadingLegislation}
                    hasMore={legislationHasMore}
                    isExpanded={false}
                    onExpand={() =>
                      onExpandCategory(SearchEntityType.Legislation)
                    }
                    maxResults={maxVisibleLegislation}
                    onResultClick={onResultClick}
                    hideExpandButton={true}
                    noMargin={true}
                    totalRecords={legislationTotalRecords}
                  />
                </div>
                {/* Button - always visible at bottom */}
                {shouldShowExpandButton(
                  legislationResults,
                  legislationHasMore,
                  maxVisibleLegislation
                ) && (
                  <div className="shrink-0">
                    {renderShowMoreButton(() =>
                      onExpandCategory(SearchEntityType.Legislation)
                    )}
                  </div>
                )}
              </div>

              {/* Workspace Documents Section */}
              <div
                ref={workspaceDocsSectionRef}
                className="flex-1 min-h-0 flex flex-col overflow-hidden"
              >
                {/* Items container - clips overflow */}
                <div className="flex-1 min-h-0 overflow-hidden">
                  <GlobalSearchCategorySection
                    entityType={SearchEntityType.WorkspaceDocument}
                    results={workspaceDocResults}
                    isLoading={isLoadingWorkspaceDocs}
                    hasMore={workspaceDocsHasMore}
                    isExpanded={false}
                    onExpand={() =>
                      onExpandCategory(SearchEntityType.WorkspaceDocument)
                    }
                    maxResults={maxVisibleWorkspaceDocs}
                    onResultClick={onResultClick}
                    hideExpandButton={true}
                    noMargin={true}
                    totalRecords={documentTotalRecords}
                  />
                </div>
                {showWorkspaceDocsNotice && (
                  <div className="shrink-0 pt-1">
                    <StaleSearchIndexNotice
                      compact
                      scope="visible"
                      staleCount={staleIndex.staleCount}
                      reindexableCount={staleIndex.reindexableCount}
                      runningCount={staleIndex.runningCount}
                      batchSize={staleIndex.batchSize}
                      onReindexAll={staleIndex.reindexAll}
                    />
                  </div>
                )}
                {/* Button - always visible at bottom */}
                {shouldShowExpandButton(
                  workspaceDocResults,
                  workspaceDocsHasMore,
                  maxVisibleWorkspaceDocs
                ) && (
                  <div className="shrink-0">
                    {renderShowMoreButton(() =>
                      onExpandCategory(SearchEntityType.WorkspaceDocument)
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Expanded mode - show single category with infinite scroll
            <div className="h-full overflow-y-auto">
              {expandedCategory === SearchEntityType.Legislation && (
                <GlobalSearchCategorySection
                  entityType={SearchEntityType.Legislation}
                  results={
                    (expandedLegislationResults?.length ?? 0) > 0
                      ? expandedLegislationResults
                      : legislationResults
                  }
                  isLoading={isLoadingLegislation || isLoadingMore}
                  hasMore={legislationHasMore}
                  isExpanded={true}
                  onBack={handleBack}
                  lastElementRef={lastElementRef}
                  onResultClick={onResultClick}
                  totalRecords={legislationTotalRecords}
                />
              )}

              {expandedCategory === SearchEntityType.WorkspaceDocument && (
                <>
                  {showWorkspaceDocsNotice && (
                    <StaleSearchIndexNotice
                      scope="visible"
                      className="mb-2"
                      staleCount={staleIndex.staleCount}
                      reindexableCount={staleIndex.reindexableCount}
                      runningCount={staleIndex.runningCount}
                      batchSize={staleIndex.batchSize}
                      onReindexAll={staleIndex.reindexAll}
                    />
                  )}
                  <GlobalSearchCategorySection
                    entityType={SearchEntityType.WorkspaceDocument}
                    results={
                      (expandedDocumentResults?.length ?? 0) > 0
                        ? expandedDocumentResults
                        : workspaceDocResults
                    }
                    isLoading={isLoadingWorkspaceDocs || isLoadingMore}
                    hasMore={workspaceDocsHasMore}
                    isExpanded={true}
                    onBack={handleBack}
                    lastElementRef={lastElementRef}
                    onResultClick={onResultClick}
                    totalRecords={documentTotalRecords}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearchResultsView;
